// De twee bouwstenen achter ontwerpregel 1 van W8 (§6):
//
//   "Een leeg veld toont WAAROM het leeg is; een leeg blok mag 'binnenkort'
//    heten mét label."
//
// Owner bij scherm 7: *"geef duidelijk aan waarom het niet kan."* Daarom staat
// hier geen generiek "—" en geen "geen data": elke lege plek draagt een reden in
// gewone taal. Wie een nieuw veld toevoegt en geen reden weet te formuleren,
// heeft geen weergaveprobleem maar een dataprobleem.

import type { CSSProperties, ReactNode } from "react";

import { color, radius, size, weight } from "../tokens";

/** Waarom een specifiek veld leeg is. Inline, op de plek van de waarde. */
export function EmptyReason({ reason, style }: { reason: string; style?: CSSProperties }) {
  return (
    <span
      title={reason}
      style={{
        fontSize: size.meta,
        color: color.faint,
        fontStyle: "italic",
        ...style,
      }}
    >
      {reason}
    </span>
  );
}

/**
 * Een heel blok dat nog niet gevuld kan worden. Draagt een zichtbaar label
 * "binnenkort" plus de reden — nooit een lege kaart en nooit een verzonnen
 * vulling.
 */
export function ComingSoon({
  title,
  reason,
  children,
}: {
  title: string;
  reason: string;
  children?: ReactNode;
}) {
  return (
    <div
      style={{
        background: color.surfaceQuote,
        border: `1px dashed ${color.border}`,
        borderRadius: radius.block,
        padding: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span
          style={{
            fontSize: size.label,
            fontWeight: weight.medium,
            color: color.faint,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {title}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: weight.medium,
            color: color.accentAmber,
            background: color.accentAmberBg,
            border: `1px solid ${color.accentAmber}33`,
            borderRadius: radius.pill,
            padding: "2px 8px",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          binnenkort
        </span>
      </div>
      <p style={{ fontSize: size.meta, color: color.soft, lineHeight: 1.55 }}>{reason}</p>
      {children}
    </div>
  );
}

/** Een lijst die niets opleverde: zeg of dat een filter is of een leegte. */
export function EmptyList({ reason, onReset }: { reason: string; onReset?: () => void }) {
  return (
    <div style={{ padding: "48px 0", textAlign: "center" }}>
      <p style={{ fontSize: size.body, color: color.faint }}>{reason}</p>
      {onReset && (
        <button
          onClick={onReset}
          style={{
            display: "block",
            margin: "10px auto 0",
            background: "none",
            border: `1px solid ${color.border}`,
            color: color.body,
            borderRadius: radius.input,
            padding: "7px 14px",
            fontSize: size.meta,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          Wis
        </button>
      )}
    </div>
  );
}

/** Een mislukte aanroep. Toont de reden uit ApiError, nooit een witte pagina. */
export function LoadError({ reason, onRetry }: { reason: string; onRetry?: () => void }) {
  return (
    <div style={{ padding: "40px 0", textAlign: "center" }}>
      <p style={{ fontSize: size.body, color: color.accentRed, marginBottom: 4 }}>
        Ophalen mislukt
      </p>
      <p style={{ fontSize: size.meta, color: color.faint }}>{reason}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            marginTop: 12,
            background: "none",
            border: `1px solid ${color.border}`,
            color: color.body,
            borderRadius: radius.input,
            padding: "7px 14px",
            fontSize: size.meta,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          Opnieuw proberen
        </button>
      )}
    </div>
  );
}
